import React, { useContext, useEffect } from 'react'; //eslint-disable-line
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import locationStoreContext from '../stores/locationStore';
import viewStoreContext from '../stores/viewStore';
import StationHeader from '../components/StationHeader';
import goTrackerConfig from 'goTrackerConfig';
import BoltIcon from '../components/BoltIcon';
import utils from '../utils';
import * as CONSTS from '../utils/constants';
import '../i18n';
import MessageOverride from '../components/MessageOverride';
import moment from 'moment';
import HeaderColumns from '../components/HeaderColumns';

const BusDeparture = observer(props => {
  const { t } = useTranslation();

  const headerCol = [
    t('busDepartures.columnHeadings.1'),
    t('busDepartures.columnHeadings.2'),
    t('busDepartures.columnHeadings.3'),
    t('busDepartures.columnHeadings.4'),
  ];

  const locationStore = useContext(locationStoreContext);
  const viewStore = useContext(viewStoreContext);

  const loadPage = () => {
    locationStore.scrollingMessages = [];
    if (!props.station) return;
    locationStore.setBusStation(props.station);
    locationStore.loadBusTripByTimingPoint(props.station);
  };

  useEffect(() => {
    loadPage();

    const interval = setInterval(() => {
      locationStore.loadBusTripByTimingPoint(props.station);

      if (goTrackerConfig.debug) {
        viewStore.updateAutoRefreshTime();
      }
    }, goTrackerConfig.autoRefreshRate || 10000);
    return () => clearInterval(interval);
  }, [locationStore.corridors]);

  useEffect(() => {
    viewStore.updateView(CONSTS.VIEW_PATHS.STATION_DEPARTURE);
  }, []);

  const getTripMessages = msg => {
    if (!msg || msg.length < 1) return '';
    return msg.map((m) => {
      if (m === undefined) {
        return (
          <p></p>
        );
      }
      const messages = {};
      const busMsg = m.body[`${viewStore.languageName.toLowerCase()}`];
      if (busMsg) {
        messages.text = busMsg;
      }
      if (messages === undefined) return '';

      const scrollingMessages = `${messages.text} `;

      return scrollingMessages;
    });
  };

  const isDelayed = (departure) => {
    if (departure && departure.schDeparture && departure.expDeparture) {
      const interval = moment
        .duration(moment(utils.toDate(departure.schDeparture)).diff(moment()))
        .asMinutes();
      const delayMin = moment
        .duration(moment(utils.toDate(departure.expDeparture)).diff(utils.toDate(departure.schDeparture)))
        .asMinutes();

      if (
        interval < goTrackerConfig.delayedStartShowRedMin &&
        delayMin >= goTrackerConfig.delayedItemsDisplayMin
      ) {
        return true;
      }
    }
    return false;
  };

  const isTISShortDelayed = (departure, isBus) => {
    if (departure && departure.schDeparture) {
      const interval = moment
        .duration(moment(utils.toDate(departure.schDeparture), true).diff(moment()))
        .asMinutes();

      if (
        interval < goTrackerConfig.delayedStartShowRedMin &&
        departure.expDeparture
      ) {
        const scheduled = moment(utils.toDate(departure.schDeparture));
        const expected = moment(utils.toDate(departure.expDeparture));

        if (expected > scheduled) {
          const delay = moment.duration(expected.diff(scheduled)).asSeconds();
          const delayThreshold = isBus
            ? goTrackerConfig.delayThreshold.b
            : goTrackerConfig.delayThreshold.t;
          if (delay >= delayThreshold) {
            return true;
          }
        }
      }
    }
    return false;
  };

  // eslint-disable-next-line no-unused-vars
  const getTISDelayClass = (departure, isBus) => {
    const isShortDelayed = isTISShortDelayed(departure, isBus);
    const isLongDelayed = isDelayed(departure);
    const isDeparted = departure.isDone;
    let delayClass = '';

    if (departure.isCancelled) {
      delayClass = 'cancelled';
    } else if (isDeparted) {
      delayClass = 'departed';
    } else if (isShortDelayed || isLongDelayed) {
      delayClass = 'delayed';
    } else {
      delayClass = 'ontime';
    }
    return delayClass;
  };

  const getDepartTime = (departure) => {
    const departTime = utils.formatTime(departure.expDeparture, true);
    return departTime;
  };

  const getDepartInTime = (departure, departureClass) => {
    const status = departureClass ? departureClass : getTISDelayClass(departure, true);
    let departTime = '-';
    switch (status) {
      case 'cancelled':
        departTime = t('tripStatus.cancelled');
        break;
      case 'departed':
        departTime = t('tripStatus.departed');
        break;
      default:
        departTime = utils.timeDiffInMins(new Date(), departure.expDepartureTimestamp,) + ' min';
    }

    return departTime;
  };

  const buildStops = stops => {
    return stops.map((stop, i) => {
      return (
        <React.Fragment key={`${stop.name} - ${i}`}>
          <div>{stop.name}</div>
        </React.Fragment>
      );
    });
  };

  const buildBusDeparture = busDept => {
    return (
      <React.Fragment>
        <HeaderColumns className="bus-departure-header-row" headerCol={headerCol}/>
        <ul>
          {(busDept?.length > 0) &&
            busDept.map((bus, i) => {
              const ariaLive = i === 0 ? 'assertive' : 'off';
              const departureClass = getTISDelayClass(bus, true);
              return (
                <li className="bus-departure-row" key={`${bus.tripName}${i}`} aria-live={ariaLive}>
                  <Link
                    to={{
                      pathname: `${viewStore.views.busDeparture}/${props.corridor}/${props.station}/${viewStore.views.busTripDetail}/${bus.tripNumber}`,
                      state: {},
                    }}
                    role="link"
                    onClick={locationStore.resetTripDetail}
                  >
                    <div className='data in-service remarks'>
                      <div aria-describedby={t('busDepartures.columnHeadings.1')}>
                        {bus.tripName} {goTrackerConfig.debug === true ? <div className="debug-container trip-number">{bus.tripNumber}</div> : ''}
                      </div>
                      <div className="schedule-col" aria-describedby={t('busDepartures.columnHeadings.2')}>
                        {utils.formatTime(bus.schDeparture, viewStore.isTwentyFourHour)}
                        {bus.isExpress ? (<span><BoltIcon /> </span>) : ('')}
                      </div>
                      <div className="schedule-col" aria-describedby={t('busDepartures.columnHeadings.3')}>
                        {bus.actTrack}
                      </div>
                      <div className="schedule-col" aria-describedby={t('busDepartures.columnHeadings.4')}>
                        <div className={`delay-status ${departureClass}`}>{getDepartInTime(bus, departureClass)} {goTrackerConfig.debug === true ? <div className="debug-container trip-number">{getDepartTime(bus)}</div> : ''}
                        </div>
                        {<i className="fa fa-chevron-right" />}
                      </div>
                    </div>
                    {bus.tripMessages && bus.tripMessages.length > 0 && (
                      <div className='remarks in-service no-occupancy message'>
                        <p>{getTripMessages(bus.tripMessages)}</p>
                      </div>
                    )}
                  </Link>
                  {/* <div className={`expected ${bus.tripStatus}`} aria-describedby={t('stationDepartures.columnHeadings.5')}>
                    {<i className="fa fa-chevron-right" />}
                  </div> */}
                </li>);
            })
          }

          {(busDept?.length === 0) && (
            <React.Fragment>
              <div className="bus-departure-row no-trip">
                <span>{t('home.endOfServiceMessage')}</span>
              </div>
            </React.Fragment>
          )
          }
        </ul>
        {false && buildStops(busDept.stop)}
      </React.Fragment>
    );
  };

  const renderNoTripMessages = (messages) => {
    if (messages && (messages.isFirstPageContainTrips === false || messages.isMoreTripsAvailableFortheDay === false)) {
      return (
        <React.Fragment>
          <HeaderColumns className="bus-departure-header-row" headerCol={headerCol}/>
          <div className="departure-row no-trip">
            {messages.isMoreTripsAvailableFortheDay === false ? (
              t('busDepartures.dataUnavailableFirstPage')
            ) : (
              t('busDepartures.dataUnavailableForNext90min')
            )}
          </div>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <HeaderColumns className="bus-departure-header-row" headerCol={headerCol}/>
        <div className="bus-departure-row no-trip">
          <i className="fa fa-spinner fa-spin" />
          {/* <span>{t('home.endOfServiceMessage')}</span> */}
        </div>
      </React.Fragment>
    );
  };

  return (
    <main id="main-content">
      <div className="selection">
        <MessageOverride isActive={true} />
        <StationHeader isBus={true} />
        {locationStore.hasBusDepartures === true ? (
          buildBusDeparture(locationStore.messages.commitmentTrips)
        ) : (
          renderNoTripMessages(locationStore.messages)
        )}
      </div>
    </main>
  );
});

export default BusDeparture;
